// Info screen (Info tab, route #/data — both kept for link stability; the tab was
// called "Settings" through v0.6). Houses Appearance (light/dark theme), the
// offline data-snapshot build, and — from v0.7 — Shows: the way into each show's
// bundled exhibitor directory. The vendor/product lookups that consume the
// snapshot live on the Vendors tab (Slice 4); this screen is settings + the build.

import { getIdentity, type Identity } from '../auth/auth';
import { renderSessionExpired } from '../auth/sessionRecovery';
import { fetchSnapshot, isNotAuthed } from '../sync/api';
import { saveSnapshot, getSnapshot } from '../db/snapshot';
import type { StoredSnapshot } from './types';
import { toast } from '../ui/toast';
import { getTheme, setTheme, type Theme } from '../ui/theme';
import { loadShows, fmtShowDates, type ShowDirectory } from '../exhibitors/data';
import { listHitList } from '../db/hitlist';

export async function renderSnapshot(root: HTMLElement): Promise<void> {
  const snap = await getSnapshot();
  const id = getIdentity();
  const theme = getTheme();

  root.innerHTML = `
    <section class="card settings">
      <h2 class="settings__h">Appearance</h2>
      <div class="field">
        <span>Theme</span>
        <div class="segmented" id="theme-toggle" role="group" aria-label="Theme">
          <button type="button" class="seg ${theme === 'dark' ? 'seg--on' : ''}" data-theme="dark">Dark</button>
          <button type="button" class="seg ${theme === 'light' ? 'seg--on' : ''}" data-theme="light">Light</button>
        </div>
        <p class="settings__hint">Light is a print-friendly, low-ink scheme. Printing always uses the light palette.</p>
      </div>
    </section>

    <section class="card snapshot">
      <div class="snapshot__head">
        <h2 class="settings__h">Data snapshot</h2>
        <p class="snapshot__hint">Pull vendors, costs &amp; size demand for offline use on the floor. Build on wifi before the trip; look them up on the <a href="#/vendors" class="link">Vendors</a> tab.</p>
      </div>
      <div id="snap-status" class="snapshot__status">${statusHtml(snap)}</div>
      <button id="snap-build" class="btn btn--primary" ${canBuild(id) ? '' : 'disabled'}>
        ${snap ? 'Rebuild snapshot' : 'Build snapshot'}
      </button>
      ${id.authenticated ? '' : '<p class="snapshot__hint">Sign in to build a snapshot.</p>'}
    </section>

    <section class="card shows">
      <div class="shows__head">
        <h2 class="settings__h">Shows</h2>
        <p class="settings__hint">Exhibitor lists ship inside the app — search them, and star booths onto the team hit list, with no signal.</p>
      </div>
      <div id="shows-body" class="shows__body"><span class="settings__hint">Loading exhibitor lists&hellip;</span></div>
    </section>
  `;

  // Filled in after first paint: the exhibitor data is a separate (lazy) chunk, and
  // the Info tab shouldn't wait on it to show Appearance + the snapshot build.
  void renderShows(root.querySelector<HTMLElement>('#shows-body')!);

  // Appearance toggle — flips the whole app and persists (see ui/theme.ts).
  const toggle = root.querySelector<HTMLElement>('#theme-toggle')!;
  toggle.querySelectorAll<HTMLButtonElement>('.seg').forEach((btn) => {
    btn.addEventListener('click', () => {
      setTheme(btn.dataset.theme as Theme);
      toggle.querySelectorAll('.seg').forEach((b) => b.classList.remove('seg--on'));
      btn.classList.add('seg--on');
    });
  });

  const statusEl = root.querySelector<HTMLElement>('#snap-status')!;
  const buildBtn = root.querySelector<HTMLButtonElement>('#snap-build')!;

  buildBtn.addEventListener('click', async () => {
    if (!navigator.onLine) {
      toast('Offline — connect to wifi to build', 'warn');
      return;
    }
    buildBtn.disabled = true;
    const label = buildBtn.textContent;
    buildBtn.textContent = 'Building… (this can take a minute)';
    statusEl.textContent = 'Contacting the server and pulling data…';
    try {
      // Interactive: a person is standing here waiting, so an expired session may
      // re-auth inline. This IS a tap handler, and every await between it and
      // loginPopup() calls the next function synchronously first, so the popup
      // still opens inside the user's gesture (iOS Safari requires that).
      const payload = await fetchSnapshot(true);
      const stored = await saveSnapshot(payload);
      statusEl.innerHTML = statusHtml(stored);
      buildBtn.textContent = 'Rebuild snapshot';
      toast(
        `Snapshot ready ✓ ${stored.counts.vendors} vendors, ${stored.counts.catalog} products`,
        'ok',
      );
    } catch (err) {
      buildBtn.textContent = label ?? 'Build snapshot';
      // Re-auth was needed and didn't land (cancelled, or the popup was blocked).
      // Offer the one-tap fix in place of the raw token error.
      if (isNotAuthed(err)) {
        renderSessionExpired(
          statusEl,
          { what: 'the snapshot', verb: 'build', className: 'session-x--inline' },
          () => buildBtn.click(),
        );
        return;
      }
      // The backend self-reports; show its message verbatim.
      const detail = err instanceof Error ? err.message : String(err);
      statusEl.textContent = detail;
      toast(detail, 'warn');
    } finally {
      buildBtn.disabled = !canBuild(getIdentity());
    }
  });
}

/**
 * The Shows list (v0.7): every show we know about, whether or not we have its
 * exhibitors. Shows with data link into their directory; a show whose exhibitors are
 * carried by another show's list (OffPrice — co-located with MAGIC, vendors not
 * separable) links into THAT directory (v1.0); anything genuinely unpulled still
 * shows with the reason rather than being quietly left off the list.
 */
async function renderShows(el: HTMLElement): Promise<void> {
  let shows: ShowDirectory[];
  let hitCount = 0;
  try {
    [shows, hitCount] = await Promise.all([loadShows(), listHitList().then((t) => t.length)]);
  } catch (err) {
    // The bundled chunk didn't load (first run offline before the SW cached it).
    console.warn('exhibitor data failed to load', err);
    el.innerHTML = `<span class="settings__hint">Exhibitor lists aren’t available on this device yet — reopen the app once on wifi.</span>`;
    return;
  }

  // Pointer rows (OffPrice → MAGIC) name their target and borrow its count, so the
  // row can say what tapping it gets you.
  const byKey = new Map(shows.map((s) => [s.key, s]));

  el.innerHTML = `
    <ul class="shows-list">
      ${[...shows].sort(byStartDate).map((s) => showRow(s, byKey)).join('')}
    </ul>
    <a href="#/hitlist" class="shows__hits">★ Hit list${hitCount ? ` (${hitCount})` : ''}</a>
  `;
}

/** Trip order — soonest show first; the ones with no dates (OffPrice) last. */
function byStartDate(a: ShowDirectory, b: ShowDirectory): number {
  const at = a.dates ? Date.parse(a.dates.split('/')[0]) : NaN;
  const bt = b.dates ? Date.parse(b.dates.split('/')[0]) : NaN;
  if (Number.isNaN(at) && Number.isNaN(bt)) return 0;
  if (Number.isNaN(at)) return 1;
  if (Number.isNaN(bt)) return -1;
  return at - bt;
}

function showRow(s: ShowDirectory, byKey: Map<string, ShowDirectory>): string {
  // A show whose exhibitors live in ANOTHER show's directory (OffPrice, co-located on
  // the MAGIC floor) is not missing data — it's one tap away. Render it as a link into
  // the host directory rather than the "Data unavailable" dead end it used to be. The
  // target is resolved defensively: if it's gone or empty, fall through to the plain
  // no-list row below rather than shipping a link into nothing.
  const via = s.see_show ? byKey.get(s.see_show) : undefined;
  if (s.see_show && via && via.count > 0) {
    const meta = [`Co-located — listed in ${via.show}`, `${via.count.toLocaleString()} exhibitors`]
      .filter(Boolean)
      .join(' · ');
    return `
      <li class="show-row show-row--via">
        <a class="show-row__link" href="#/show/${encodeURIComponent(via.key)}">
          <div class="show-row__name">${esc(s.label)} <span class="show-row__via">→ see ${esc(via.show)}</span></div>
          <div class="show-row__meta">${esc(meta)}</div>
          ${s.unavailable ? `<div class="show-row__why">${esc(s.unavailable)}</div>` : ''}
        </a>
      </li>`;
  }

  if (s.unavailable) {
    return `
      <li class="show-row show-row--off">
        <div class="show-row__name">${esc(s.label)}</div>
        <div class="show-row__meta">Data unavailable</div>
        <div class="show-row__why">${esc(s.unavailable)}</div>
      </li>`;
  }

  const meta = [
    `${s.count.toLocaleString()} exhibitors`,
    fmtShowDates(s.dates),
    s.has_booths ? '' : 'names only — booths not assigned yet',
  ].filter(Boolean);

  return `
    <li class="show-row">
      <a class="show-row__link" href="#/show/${encodeURIComponent(s.key)}">
        <div class="show-row__name">${esc(s.label)}</div>
        <div class="show-row__meta">${esc(meta.join(' · '))}</div>
      </a>
    </li>`;
}

function statusHtml(snap: StoredSnapshot | undefined): string {
  if (!snap) return '<span class="snapshot__none">No snapshot on this device yet.</span>';
  const built = new Date(snap.built_at).toLocaleString();
  return `
    <div class="snapshot__meta">
      <strong>Cached ✓</strong>
      <span>${snap.counts.vendors} vendors · ${snap.counts.catalog} products · ${snap.counts.size_shares} size rows</span>
      <span>built ${esc(built)} by ${esc(snap.built_by || '—')}</span>
    </div>`;
}

/**
 * May this identity attempt a snapshot build?
 *
 * Deliberately includes the EXPIRED state. Through v1.1 the button was gated on
 * `authenticated` alone, so the moment a refresh token died the one control that
 * could have fixed it went grey — the user's only route back was to sign out and
 * in again, which is exactly the workaround this release removes. An expired tap
 * re-auths and then builds.
 */
function canBuild(id: Identity): boolean {
  return id.authenticated || id.expired;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
